import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  { id: 'imphaleast', name: 'Imphal East', homestays: 250, valleys: ['Kangla Fort', 'INA Memorial', 'Andro Village', 'Khonghampat'], altitude: '780–1,200m', highlight: true, division: 'Valley' },
  { id: 'imphalwest', name: 'Imphal West', homestays: 220, valleys: ['Ima Market', 'Govindajee Temple', 'Langthabal', 'Wangkhei'], altitude: '780–1,000m', highlight: true, division: 'Valley' },
  { id: 'bishnupur', name: 'Bishnupur', homestays: 180, valleys: ['Loktak Lake', 'Keibul Lamjao NP', 'Sendra Island', 'Phubala'], altitude: '780–900m', highlight: true, division: 'Valley' },
  { id: 'ukhrul', name: 'Ukhrul', homestays: 130, valleys: ['Shirui Peak', 'Shirui Lily', 'Khayang Waterfall', 'Hundung'], altitude: '1,500–2,600m', highlight: true, division: 'Hills' },
  { id: 'churachandpur', name: 'Churachandpur', homestays: 150, valleys: ['Tipaimukh', 'Ngaloi Falls', 'Leisan', 'Tuibong'], altitude: '900–2,200m', highlight: true, division: 'Hills' },
  { id: 'senapati', name: 'Senapati', homestays: 120, valleys: ['Mao Gate', 'Dzükou Entry', 'Maram Khullen', 'Purul'], altitude: '1,500–2,900m', highlight: false, division: 'Hills' },
  { id: 'tamenglong', name: 'Tamenglong', homestays: 100, valleys: ['Zeilad Lake', 'Buning Meadow', 'Tharon Caves', 'Barak Falls'], altitude: '500–2,000m', highlight: false, division: 'Hills' },
  { id: 'chandel', name: 'Chandel', homestays: 70, valleys: ['Moreh Border', 'Tengnoupal', 'Yangoupokpi', 'Mollen'], altitude: '500–1,800m', highlight: false, division: 'Hills' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Manipur Tourism Department, from the Loktak floating islands to the Shirui hills, digitized and discoverable.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on valley map', 'Basic contact listing', 'Searchable by valley & hill district'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified. Verified lakeside stays, hill-country retreats, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The jewels of the Jewelled Land.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay from Loktak\'s floating islands to Ukhrul\'s Shirui hills — searchable, verified, bookable on a single platform.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, Sangai Festival trends, and seasonal occupancy insights for Manipur tourism planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy — empowering Meitei, Naga, and Kuki communities to share their ancient valley civilization.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification ensuring each homestay honours the Meitei traditions, polo heritage, and the sacred Loktak ecosystem.' },
];

function ManipurMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'senapati': { d: 'M120 30 L250 25 L280 70 L260 120 L180 130 L120 100 L100 60 Z', cx: 190, cy: 78 },
    'ukhrul': { d: 'M260 120 L280 70 L380 60 L400 110 L380 160 L320 170 L280 150 Z', cx: 330, cy: 115 },
    'tamenglong': { d: 'M30 80 L100 60 L120 100 L110 160 L70 190 L30 170 Z', cx: 75, cy: 128 },
    'imphalwest': { d: 'M110 160 L180 130 L210 160 L200 200 L150 210 L110 195 Z', cx: 160, cy: 178 },
    'imphaleast': { d: 'M210 160 L280 150 L320 170 L310 215 L260 225 L220 210 L200 200 Z', cx: 260, cy: 190 },
    'bishnupur': { d: 'M110 195 L150 210 L200 200 L220 210 L210 260 L160 270 L100 250 L80 220 Z', cx: 155, cy: 235 },
    'churachandpur': { d: 'M80 220 L100 250 L160 270 L170 320 L130 350 L60 340 L30 290 Z', cx: 100, cy: 295 },
    'chandel': { d: 'M260 225 L310 215 L380 230 L390 290 L350 320 L280 310 L240 280 L210 260 Z', cx: 310, cy: 270 },
  };

  return (
    <svg viewBox="0 0 430 370" className="w-full max-w-2xl mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id} onMouseEnter={() => setActiveDistrict(id)} onMouseLeave={() => setActiveDistrict(null)} style={{ cursor: 'pointer' }}>
            <path d={path.d}
              fill={isActive ? '#9B7EC8' : isHighlight ? 'rgba(155,126,200,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#C4A8E8' : 'rgba(255,255,255,0.3)'} strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }} />
            <text x={path.cx} y={path.cy} textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'} fontSize={isActive ? 10 : 8}
              fontFamily="Georgia, serif" style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}>
              {district?.name}
            </text>
            {isActive && (
              <text x={path.cx} y={path.cy + 14} textAnchor="middle" dominantBaseline="middle"
                fill="#C4A8E8" fontSize={8} fontFamily="Georgia, serif" style={{ pointerEvents: 'none' }}>
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function ManipurStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="manipur-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="manipur" />
        </div>
      </nav>

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1600908120729-79f720ae82fa?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Manipur &middot; The Jewelled Land</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 16 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where islands float on ancient lakes,<br />and the world's rarest deer dances at dawn
          </h2>
        </div>
      </section>

      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#9B7EC8] text-[10px] tracking-[0.5em] uppercase mb-6">The Floating Kingdom</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            An oval valley ringed by hills,<br />
            where polo was born and dance is prayer.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            On Loktak Lake, the largest freshwater lake in Northeast India, circular floating islands 
            of matted vegetation — phumdis — drift like green worlds, home to fishermen who have 
            built their lives upon water. In Keibul Lamjao, the world's only floating national park, 
            the endangered Sangai deer — Manipur's dancing deer — steps delicately across these phumdis 
            at dawn. At Ima Keithel in Imphal, the world's largest all-women market, over 5,000 Meitei 
            women trade everything from fresh vegetables to handwoven fabrics in a tradition stretching 
            back five centuries. In Ukhrul, the Shirui Lily blooms only on one hilltop on Earth. Modern 
            polo traces its origins to Sagol Kangjei, played in this valley for over 3,000 years. 
            Manipur is where ancient civilization and untouched wilderness share the same heartbeat.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#9B7EC8] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Valley floor to hill summits. One jewel.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative"><ManipurMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} /></div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#9B7EC8] text-[10px] tracking-[0.4em] uppercase">Region</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division}</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-white/40" /><span className="text-white/60 text-sm">{activeData.altitude}</span></div>
                    <div className="flex flex-wrap gap-2">{activeData.valleys.map(v => (<span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>))}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a region to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer" onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Manipur</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#9B7EC8] to-[#C4A8E8] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#9B7EC8] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">{tier.features.map(f => (<li key={f} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} /><span className="text-white/55 text-sm">{f}</span></li>))}</ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#9B7EC8] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">GoHimalayan serves as the technology and marketing partner — bringing every licensed homestay across Manipur's valley and hills online and discoverable.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#9B7EC8] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#9B7EC8]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(155,126,200,0.05), transparent)' }}>
            <p className="text-[#9B7EC8] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#9B7EC8]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#9B7EC8] text-xs">1</span></div>
                <p className="text-white/60 text-sm">Manipur shares its licensed homestay registry — GoHimalayan digitizes and maps every property across valley and hill districts</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#9B7EC8]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#9B7EC8] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into Loktak Lake communities, Shirui hills, and valley homestays</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#9B7EC8]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#9B7EC8] text-xs">3</span></div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, Sangai Festival analytics, and eco-tourism planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Manipur."
              className="inline-flex items-center gap-3 bg-[#9B7EC8] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#b49ad8] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#9B7EC8" />
      <style>{`@keyframes expandBar { from { width: 0; } to { width: 100%; } }`}</style>
    </div>
  );
}
